import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Field, reduxForm } from 'redux-form'
import * as securePin from 'secure-pin'
import classNames from 'classnames'
import { uuid } from 'uuidv4'
import { isEqual, isUndefined, get, isEmpty, sortBy, findKey, includes, omit } from 'lodash'

import { MENU_TYPES } from '../../utils/menuTypes'
import { employeeRoleOptions } from '../../utils/employeeRoles'
import Validation from '../../utils/validations'

import CheckboxGroupItem from '../CheckboxGroupItem'
import DropdownMenu from '../DropdownMenu'
import Modal from '../Modal'
import Textbox from '../Textbox'
import ToggleButton from '../ToggleButton'

let StaffDetails = ({
  allMenuSubTypes,
  attendant,
  attendantMenuSubtypes,
  attendantSetting,
  attendantStandUuids,
  currentVenueUuid,
  formAltered,
  formFields,
  formStandUuids,
  formSubTypes,
  formValues,
  history,
  initialRole,
  loadAttendantSettings,
  onClose,
  onCreate,
  onFieldValueChange,
  onMount,
  onUnmount,
  onCreateEmployeeRole,
  onUpdateEmployeeRole,
  removeAttendant,
  staffId,
  updateAttendantSetting,
  updateSuitesStaff,
}) => {
  const [roleSelected, setRoleSelected] = useState(initialRole)
  const isSupervisorOrManager = includes(['manager', 'supervisor'], get(roleSelected, 'value'))

  useEffect(() => {
    if (!isEmpty(currentVenueUuid)) {
      onMount(currentVenueUuid, staffId)
    }

    return () => onUnmount()
  }, [currentVenueUuid, staffId, onMount, onUnmount])

  useEffect(() => {
    const attendantParams = { pin_number: get(attendant, 'pinNumber') }

    if (!isEmpty(attendantParams.pin_number && !isEmpty(currentVenueUuid))) {
      loadAttendantSettings(currentVenueUuid, attendantParams)
    }
  }, [loadAttendantSettings, attendant, currentVenueUuid])

  const disableUpdateButton = () => {
    let disableButton = true
    const updatedFieldsExist = formFields.map(field => formAltered(field)).includes(true) || selectedMenuTypesAltered || attendantAssignedToSuitesAltered
    disableButton = !updatedFieldsExist

    formFields.forEach(field => {
      if (formAltered(field)) {
        const value = get(formValues, field)

        if (field === 'email' && !isUndefined(Validation.emailIsValid(value))) {
          disableButton = true
        }

        if (field === 'phone' && !isUndefined(Validation.phoneNumberIsValid(value))) {
          disableButton = true
        }
      }
    })

    if (roleSelected?.value !== initialRole?.value) {
      disableButton = false
    }

    if (!roleSelected?.value) {
      disableButton = true
    }

    return disableButton
  }

  const onSubmit = () => { // combine update/create
    const attendantUuid = staffIdIsNew ? uuid() : get(attendant, 'uuid', '')
    let attendantParams = { uuid: attendantUuid }
    let attendantSettingParams = { uuid: staffIdIsNew ? uuid() : get(attendantSetting, 'uuid', ''), employeeUuid: attendantUuid }

    const isRoleChanged = roleSelected !== initialRole

    if (isRoleChanged || selectedMenuTypesAltered || attendantAssignedToSuitesAltered) { // updating attendant setting
      const menuSubtypes = formSubTypes.map(type => findKey(MENU_TYPES, (menu) => menu === type))

      const supervisorOrManagerConfig = {
        ...(omit(get(attendantSetting, 'configuration', {}), ['menuSubtypes', 'standUuids'])),
        menuSubtypes: null,
        standUuids: null,
      }

      const cartConfig = {
        ...get(attendantSetting, 'configuration', {}),
        menuSubtypes: menuSubtypes,
        standUuids: formStandUuids,
      }

      attendantSettingParams = {
        ...attendantSettingParams,
        configuration: isSupervisorOrManager ? supervisorOrManagerConfig : cartConfig,
        type: "AppDeviceConfiguration::AttendantSetting"
      }

      if (!staffIdIsNew) updateAttendantSetting(attendantSettingParams)
    }
    // updating attendant
    const alteredFieldsExistArray = []

    formFields.forEach(field => {
      if (formAltered(field)) {
        let value = get(formValues, field)

        if (field === 'deactivated') {
          const deactivatedValue = value === true && attendant.deactivatedAt === null
            ? new Date()
            : null

          attendantParams = {
            ...attendantParams,
            deactivatedAt: deactivatedValue,
          }

        } else if (field === 'firstName' || field === 'lastName') {
          attendantParams = {
            ...attendantParams,
            name: get(formValues, 'name')
          }
        } else {
          attendantParams = {
            ...attendantParams,
            [field]: value
          }
        }

        alteredFieldsExistArray.push(true)
      }
    })

    // update attendant only if attendant attributes have been altered
    if (!staffIdIsNew && alteredFieldsExistArray.includes(true)) updateSuitesStaff(attendantParams)

    if (staffIdIsNew) {
      attendantSettingParams.type = 'AppDeviceConfiguration::AttendantSetting'
      const menuSubtypes = formSubTypes.map(type => findKey(MENU_TYPES, (menu) => menu === type))

      const configuration = {
        ...get(attendantSetting, 'configuration', {}),
        menuSubtypes: isSupervisorOrManager ? null : menuSubtypes,
        standUuids: isSupervisorOrManager ? null : formStandUuids,
      }

      attendantSettingParams = {
        ...attendantSettingParams,
        configuration,
      }

      const attendantAndAttendantSettingParams = {
        attendantParams,
        attendantSettingParams,
      }

      onCreate(currentVenueUuid, attendantAndAttendantSettingParams, roleSelected?.value)
      history.push('/staff')
    }

    if (roleSelected?.value && !staffIdIsNew) {
      const employeeRoleParams = {
        employeeUuid: attendant?.uuid,
        uuid: uuid(),
        role: roleSelected?.value,
      }

      if (isEmpty(initialRole)){
        onCreateEmployeeRole(employeeRoleParams)
      } else if (roleSelected?.value !== initialRole?.value) {
        onUpdateEmployeeRole(employeeRoleParams, initialRole?.deleteId)
      }
    }
  }

  const onSubtypeChange = (e) => {
    const subType = e.target.id
    let newFormSubTypes

    // TODO(mkramerl): This is storing the natural language version of the menu subtype; make sure
    // the POS will handle that or update what is being sent here.
    if (e.target.checked) {
      newFormSubTypes = [ ...formSubTypes, subType ]
      onFieldValueChange('subTypes', newFormSubTypes)
    } else {
      newFormSubTypes = formSubTypes.filter(type => type !== subType)
      onFieldValueChange('subTypes', newFormSubTypes)
    }
  }

  const onRoleChange = e => {
    const { value } = e.target
    const role = employeeRoleOptions.find(emplyeeRole => emplyeeRole.value === value)

    setRoleSelected(role)
    if(includes(['manager', 'supervisor'], get(value, 'value'))) {
      onFieldValueChange('subTypes', [])
    }
  }

  const selectedMenuTypesAltered = !isEqual(sortBy(attendantMenuSubtypes), sortBy(formSubTypes))
  const attendantAssignedToSuitesAltered = !isEqual(sortBy(attendantStandUuids), sortBy(formStandUuids))
  const staffIdIsNew = staffId === 'new'
  const submitButtonClassName = staffIdIsNew ? '-new' : '-update'

  const allowOnlyNumbers = val => (val || '').replace(/[^\d]/g, '')

  return (
    <Modal title="Staff Settings" onClose={onClose}>
      <div className='attendant-settings'>
        <div className='first-name'>
          <Field
            name='firstName'
            component={Textbox}
            label='First Name'
            required={true}
            onChange={(e)=> {
              const firstName = e.target.value
              const lastName = get(formValues, 'lastName')

              onFieldValueChange('firstName', firstName)
              onFieldValueChange('name', `${firstName} ${lastName}`)
            }}
          />
        </div>
        <div className='last-name'>
          <Field
            name='lastName'
            component={Textbox}
            label='Last Name'
            required={true}
            onChange={(e) => {
              const lastName = e.target.value;
              const firstName = get(formValues, 'firstName')

              onFieldValueChange('lastName', lastName)
              onFieldValueChange('name', `${firstName} ${lastName}`)
            }}
          />
        </div>
        <div className='email'>
          <Field
            name='email'
            component={Textbox}
            label='Email'
            validate={Validation.emailIsValid}
          />
        </div>
        <div className='number'>
          <Field
            name='phone'
            component={Textbox}
            label='Phone Number'
            validate={Validation.phoneNumberIsValid}
          />
        </div>
        <div className='pin'>
          <span className='pos-pin'>
            <Field
              component={Textbox}
              name='pinNumber'
              label='POS PIN'
              normalize={allowOnlyNumbers}
              maxLength="5"
            />
          </span>
          <span className='pin-reset'>
            <div
              onClick={() => {
                return securePin.generatePin(5, (pin) => {
                  onFieldValueChange('pinNumber', pin)
                })
              }}
            >
              Reset
            </div>
          </span>
        </div>
        <div className='employee-id'>
          <Field
            name='employeeId'
            component={Textbox}
            label='Employee ID'
          />
        </div>
        <DropdownMenu
          value={roleSelected?.value ?? null}
          handleChange={onRoleChange}
          className='-full-width'
          name='roles'
          includeEmpty={true}
          label='Select Role'
          options={employeeRoleOptions}
        />
        <div className='menus'>
          <div className='menus__header'>Menus</div>
          <div>
            {allMenuSubTypes.map((menu, idx) => {
              const checked = isSupervisorOrManager ? true : formSubTypes.includes(menu)

              return (
                <CheckboxGroupItem
                  isIcon={false}
                  className="-grouped"
                  key={idx}
                  id={menu}
                  onChange={onSubtypeChange}
                  checked={checked}
                  label={menu}
                  disabled={isSupervisorOrManager}
                />
              )
            })}
          </div>
        </div>
        {!staffIdIsNew && (
          <div className='toggle-wrapper -deactivate-wrapper'>
            <div className='main-switch'>
              <span className='toggle-header'>
                Deactivate account
              </span>
              <span className='toggle-value'>
                <Field
                  component={ToggleButton}
                  name='deactivated'
                />
              </span>
            </div>
            <span className='description'>Accounts may not be deleted for reporting and audits.</span>
          </div>
        )}
        <button
          className={classNames('btn', submitButtonClassName)}
          onClick={onSubmit}
          disabled={disableUpdateButton()}
        >
          Save
        </button>
      </div>
    </Modal>
  )
}

StaffDetails.defaultProps = {
  attendant: {},
  attendantMenuSubtypes: [],
  attendantSetting: {},
  currentVenueUuid: '',
  formAltered: () => {},
  formFields: [],
  formSubTypes: [],
  formValues: {},
  loadAttendantSettings: () => {},
  onCreate: () => {},
  onFieldValueChange: () => {},
  onMount: () => {},
  onUnmount: () => {},
  removeAttendant: () => {},
  updateAttendantSetting: () => {},
  updateSuitesStaff: () => {},
}

StaffDetails.propTypes = {
  attendant: PropTypes.object,
  attendantMenuSubtypes: PropTypes.array,
  attendantSetting: PropTypes.object,
  currentVenueUuid:PropTypes.string,
  formAltered: PropTypes.func,
  formFields: PropTypes.array,
  formSubTypes: PropTypes.array,
  formValues: PropTypes.object,
  loadAttendantSettings: PropTypes.func,
  onCreate: PropTypes.func,
  onFieldValueChange: PropTypes.func,
  onMount: PropTypes.func,
  onUnmount: PropTypes.func,
  removeAttendant: PropTypes.func,
  updateAttendantSetting: PropTypes.func,
  updateSuitesStaff: PropTypes.func
}

StaffDetails = reduxForm({
  form: 'attendant-settings',
  enableReinitialize: true,
  keepDirtyOnReinitialize: true
})(StaffDetails)

export default StaffDetails
